import { useEffect, useState } from 'react';

import { Modal, Button, TextButton, Input, Badge, Heading } from '../../shared/ui';

interface SubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  planPrice: number;
  billingCycle: 'monthly' | 'annual';
  walletBalance: number;
  durationDays?: number | string;
  mode?: 'plan' | 'addon';
  currencySymbol?: string;
  maxBonusPercent?: number;
  onConfirm: (bonusAmount: number) => void;
}

export function SubscribeModal({
  open,
  onOpenChange,
  planName,
  planPrice,
  billingCycle,
  walletBalance,
  durationDays,
  mode = 'plan',
  currencySymbol,
  maxBonusPercent = 100,
  onConfirm,
}: SubscribeModalProps) {
  const [bonusAmount, setBonusAmount] = useState<number | null>(0);
  const [error, setError] = useState('');

  const currencyMark = getCurrencyMark(currencySymbol);
  const normalizedMaxBonusPercent = Math.max(0, Math.min(100, maxBonusPercent));
  const maxBonusByPercent = Math.round(planPrice * (normalizedMaxBonusPercent / 100));
  const maxBonusUsage = Math.max(0, Math.min(walletBalance, planPrice, maxBonusByPercent));
  const appliedBonus = Math.min(bonusAmount ?? 0, maxBonusUsage);
  const remainingToPay = Math.max(0, planPrice - appliedBonus);
  const bonusInputDisabled = maxBonusUsage === 0;

  useEffect(() => {
    if (open) {
      setBonusAmount(0);
      setError('');
    }
  }, [open]);

  const handleBonusAmountChange = (value: string) => {
    if (bonusInputDisabled) {
      return;
    }

    setError('');
    if (value === '') {
      setBonusAmount(null);
      return;
    }

    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
      return;
    }

    if (numValue < 0) {
      setBonusAmount(0);
      setError('Сумма не может быть отрицательной');
      return;
    }

    const normalizedValue = Math.max(0, Math.floor(numValue));
    setBonusAmount(normalizedValue);

    if (normalizedValue > maxBonusUsage) {
      setError(`Максимум для оплаты бонусами — ${formatAmount(maxBonusUsage)} ${currencyMark}`);
    }
  };

  const handleBonusBlur = () => {
    if (bonusInputDisabled) {
      return;
    }

    if (bonusAmount === null) {
      setBonusAmount(0);
      return;
    }

    if (bonusAmount > maxBonusUsage) {
      setBonusAmount(maxBonusUsage);
      setError('');
    }
  };

  const handleUseMax = () => {
    if (!bonusInputDisabled) {
      setBonusAmount(maxBonusUsage);
      setError('');
    }
  };

  const handleConfirm = () => {
    if (error) {
      return;
    }

    onConfirm(appliedBonus);
  };

  const titleText = mode === 'addon' ? 'Подключение дополнения' : 'Подключение тарифа';
  const durationLabel = typeof durationDays === 'number' && durationDays > 0 ? `${durationDays} дней` : null;

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      size="sm"
      closeOnBackdrop
      title={titleText}
      footer={
        <>
          <Button variant="neutral" size="md" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button variant="dark" size="md" onClick={handleConfirm} disabled={!!error}>
            {remainingToPay > 0 ? `Оплатить ${formatAmount(remainingToPay)} ${currencyMark}` : 'Активировать подписку'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Plan Details */}
        <div className="rounded-card border border-line bg-surface-muted p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Heading size="lg" as="h4">
                {planName}
              </Heading>
            </div>
            {durationLabel && (
              <Badge tone="primary">
                <span className="flex items-center gap-1">
                  <DurationIcon />
                  {durationLabel}
                </span>
              </Badge>
            )}
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-display-m text-primary">
              {formatAmount(planPrice)} {currencyMark}
            </span>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="flex items-center gap-3 rounded-card border border-surface-muted bg-surface-muted p-4">
          <span className="flex size-10 items-center justify-center rounded-control border border-surface-muted bg-white text-primary">
            <WalletIcon />
          </span>
          <div>
            <p className="text-ui text-ink-muted">Бонусный баланс</p>
            <p className="text-heading-md text-ink">
              {formatAmount(walletBalance)} {currencyMark}
            </p>
          </div>
        </div>

        {/* Bonus Amount Input */}
        <div className="space-y-2">
          <div className="w-full">
            <Input
              size="lg"
              label={
                bonusInputDisabled
                  ? 'Бонусные рубли недоступны'
                  : `Использовать бонусные рубли (макс. ${formatAmount(maxBonusUsage)} ${currencyMark})`
              }
              type="number"
              min={0}
              max={maxBonusUsage}
              value={bonusAmount ?? ''}
              onChange={(e) => handleBonusAmountChange(e.target.value)}
              onBlur={handleBonusBlur}
              placeholder="0"
              disabled={bonusInputDisabled}
              error={error}
              icon={{
                type: 'suffix',
                icon: (
                  <div className="flex items-center gap-2">
                    <span className="text-ink-subtle">{currencyMark}</span>
                    <TextButton size="sm" tone="primary" onClick={handleUseMax} disabled={bonusInputDisabled}>
                      Макс
                    </TextButton>
                  </div>
                ),
              }}
            />
          </div>
        </div>

        {/* Payment Breakdown */}
        <div className="space-y-3 rounded-card border border-surface-muted bg-surface-muted p-4">
          <div className="flex items-center justify-between text-ui">
            <span className="text-ink-muted">Стоимость подписки</span>
            <span className="text-ink">
              {formatAmount(planPrice)} {currencyMark}
            </span>
          </div>
          {appliedBonus > 0 && (
            <div className="flex items-center justify-between text-ui">
              <span className="text-ink-muted">Бонусные рубли</span>
              <span className="text-success">
                -{formatAmount(appliedBonus)} {currencyMark}
              </span>
            </div>
          )}
          <div className="h-px bg-line-strong" />
          <div className="flex items-center justify-between text-ui">
            <span className="text-ink font-medium">К оплате</span>
            <span className="text-display-s text-ink">
              {formatAmount(remainingToPay)} {currencyMark}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const formatAmount = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(value)));

const getCurrencyMark = (currencySymbol?: string) => {
  if (!currencySymbol) return '₽';

  const normalized = currencySymbol.toUpperCase().trim();

  if (['RUB', 'RUR', '₽', 'РУБ'].includes(normalized)) return '₽';
  if (['USD', '$'].includes(normalized)) return '$';
  if (['EUR', '€'].includes(normalized)) return '€';

  return currencySymbol;
};

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
    <rect x="3" y="6" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M17 10h4v6h-4a2 2 0 0 1 0-6Z" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="17" cy="13" r="1" fill="currentColor" />
  </svg>
);

const DurationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-3" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
